import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { ControlledCardsListSkeleton } from "Components/Shared/CardsList/ControlledCardsListSkeleton";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { FormSection } from "Components/Shared/FormSection";
import { ContactPersonsContent } from "Components/Shared/Merchant/ContactPersons/ContactPersonsContent";
import { RelationType } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { useResource } from "Translations/Resources";
import { Resources } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Merchant.ContactPersons;

type Props = {
  publicPartyID: string;
  isReadOnly?: boolean;
  title?: string;
  noItemsText?: string;
};

export const ContactPersons = ({
  publicPartyID,
  isReadOnly,
  title,
  noItemsText,
}: Props) => {
  const { data: partyData, isLoading } = usePartyQuery(publicPartyID);
  const { t } = useResource();

  const sectionTitle = title ?? t(COMPONENT_RESOURCES.Title);

  if (isLoading) {
    return (
      <Layout title={sectionTitle}>
        <ControlledCardsListSkeleton />
      </Layout>
    );
  }

  if (!partyData) {
    return (
      <Layout title={sectionTitle}>
        <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>
      </Layout>
    );
  }

  const relatedContactPersonParties = partyData.relatedParties
    .filter(({ relationType }) => relationType === RelationType.ContactPerson)
    .map(({ relatedPartyPublicID }) => relatedPartyPublicID);

  return (
    <Layout title={sectionTitle}>
      <ContactPersonsContent
        isReadOnly={isReadOnly}
        owningPartyID={publicPartyID}
        relatedPartiesIDs={relatedContactPersonParties}
        noItemsText={noItemsText}
      />
    </Layout>
  );
};

type LayoutProps = {
  children: ReactNode;
  title: string;
};

const Layout = ({ children, title }: LayoutProps) => (
  <FormSection legend={title}>{children}</FormSection>
);
